/**
 * Shared state for match pick rows (1X2 + other markets).
 * Used by MatchCard and FriendChallengeMatchRows to avoid duplicating 1X2 correct/wrong and otherPicksByMarket logic.
 */

import type { MatchBet, MatchPick } from '../types/friends.types';
import { getMatchDisplayStatusUpper } from '../../challenges/helpers/matchDisplayStatus.helper';
import { getMatchResult, getPredictionDisplay } from './friendStatsPrediction.helper';
import { getTeamDisplayName } from '../../../helpers/teamKit.helper';
import {
  MATCH_WINNER_MARKET_ID,
  PICK_OUTCOME_LABELS,
} from '../../challenges/constants/challenges.constants';

const TERMINAL_MATCH_STATUSES = new Set(['FINISHED', 'CANCELLED', 'POSTPONED', 'ABANDONED']);

export interface OtherPickRow {
  marketName: string;
  user?: MatchPick;
  friend?: MatchPick;
}

export interface MatchBetRowState {
  isFinished: boolean;
  /** In-play row: show running score and suppress final-only settlement UI */
  isLive: boolean;
  matchResult: 'home' | 'away' | 'draw' | null;
  homeTeamName: string;
  awayTeamName: string;
  userPred: ReturnType<typeof getPredictionDisplay>;
  friendPred: ReturnType<typeof getPredictionDisplay>;
  score: string;
  user1x2Pick: MatchPick | undefined;
  friend1x2Pick: MatchPick | undefined;
  otherPicksByMarket: [number, OtherPickRow][];
  has1x2Picks: boolean;
  user1x2Correct: boolean | undefined;
  user1x2Wrong: boolean | undefined;
  friend1x2Correct: boolean | undefined;
  friend1x2Wrong: boolean | undefined;
  my1x2Label: string | undefined;
  friend1x2Label: string | undefined;
  friend1x2Odds: number | undefined;
}

function formatScore(home: number | null, away: number | null): string {
  if (home === null || away === null) return '';
  return `${home} - ${away}`;
}

/**
 * Derive all state needed to render pick rows for a single match (1X2 + other markets).
 */
export function getMatchBetRowState(match: MatchBet): MatchBetRowState {
  const rawStatus = String(match.matchResult.status ?? '').trim().toUpperCase();
  const displayStatus = getMatchDisplayStatusUpper(match.matchResult.status, match.date);
  const isLive = displayStatus === 'LIVE' || displayStatus === 'IN_PLAY';
  const isFinished =
    TERMINAL_MATCH_STATUSES.has(rawStatus) &&
    match.matchResult.homeScore !== null &&
    match.matchResult.awayScore !== null;

  const matchResult = getMatchResult(
    match.matchResult.homeScore,
    match.matchResult.awayScore
  );

  const homeTeamName = getTeamDisplayName(match.homeTeam);
  const awayTeamName = getTeamDisplayName(match.awayTeam);
  const userPred = getPredictionDisplay(
    match.userPrediction.outcome,
    homeTeamName,
    awayTeamName,
    match.odds,
    matchResult,
    isFinished
  );
  const friendPred = getPredictionDisplay(
    match.friendPrediction.outcome,
    homeTeamName,
    awayTeamName,
    match.odds,
    matchResult,
    isFinished
  );

  const score = isFinished
    ? formatScore(match.matchResult.homeScore, match.matchResult.awayScore)
    : '';

  const user1x2Pick = (match.userPicks || []).find((p) => p.marketId === MATCH_WINNER_MARKET_ID);
  const friend1x2Pick = (match.friendPicks || []).find((p) => p.marketId === MATCH_WINNER_MARKET_ID);
  const otherUserPicks = (match.userPicks || []).filter((p) => p.marketId !== MATCH_WINNER_MARKET_ID);
  const otherFriendPicks = (match.friendPicks || []).filter((p) => p.marketId !== MATCH_WINNER_MARKET_ID);

  const byMarket = new Map<number, OtherPickRow>();
  otherUserPicks.forEach((p) => {
    let entry = byMarket.get(p.marketId);
    if (!entry) {
      entry = { marketName: p.marketName };
      byMarket.set(p.marketId, entry);
    }
    entry.user = p;
    entry.marketName = p.marketName;
  });
  otherFriendPicks.forEach((p) => {
    let entry = byMarket.get(p.marketId);
    if (!entry) {
      entry = { marketName: p.marketName };
      byMarket.set(p.marketId, entry);
    }
    entry.friend = p;
    entry.marketName = p.marketName;
  });
  const otherPicksByMarket = Array.from(byMarket.entries());

  const has1x2Picks = user1x2Pick != null || friend1x2Pick != null;

  const user1x2Correct =
    user1x2Pick?.isCorrectOutcome === true ||
    (isFinished &&
      user1x2Pick?.isCorrectOutcome == null &&
      match.userPrediction?.outcome != null &&
      matchResult !== null &&
      match.userPrediction.outcome === matchResult);
  const user1x2Wrong =
    user1x2Pick?.isCorrectOutcome === false ||
    (isFinished &&
      user1x2Pick?.isCorrectOutcome == null &&
      match.userPrediction?.outcome != null &&
      matchResult !== null &&
      match.userPrediction.outcome !== matchResult);
  const friend1x2Correct =
    isFinished && friend1x2Pick != null
      ? friend1x2Pick.isCorrectOutcome === true ||
        (friend1x2Pick.isCorrectOutcome == null &&
          match.friendPrediction?.outcome != null &&
          matchResult !== null &&
          match.friendPrediction.outcome === matchResult)
      : undefined;
  const friend1x2Wrong =
    isFinished && friend1x2Pick != null
      ? friend1x2Pick.isCorrectOutcome === false ||
        (friend1x2Pick.isCorrectOutcome == null &&
          match.friendPrediction?.outcome != null &&
          matchResult !== null &&
          match.friendPrediction.outcome !== matchResult)
      : undefined;

  const my1x2Label =
    match.userPrediction?.outcome != null
      ? PICK_OUTCOME_LABELS[match.userPrediction.outcome]
      : undefined;
  const friend1x2Label =
    match.friendPrediction?.outcome != null
      ? PICK_OUTCOME_LABELS[match.friendPrediction.outcome]
      : undefined;
  const friend1x2Odds = friendPred.odds ?? undefined;

  return {
    isFinished,
    isLive,
    matchResult,
    homeTeamName,
    awayTeamName,
    userPred,
    friendPred,
    score,
    user1x2Pick,
    friend1x2Pick,
    otherPicksByMarket,
    has1x2Picks,
    user1x2Correct,
    user1x2Wrong,
    friend1x2Correct,
    friend1x2Wrong,
    my1x2Label,
    friend1x2Label,
    friend1x2Odds,
  };
}
